import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import MetadataFooter from "@/components/MetadataFooter";
import AttachmentsPanel from "@/components/AttachmentsPanel";
import { Plus, HandHeart, MapPin, Calendar, Users, X, Pencil, Check, UserPlus, UserMinus, Loader2, Trash2 } from "lucide-react";

const ACTIVITY_TYPES = [
  { value: "outreach_event", label: "Community event" },
  { value: "community_meeting", label: "Community meeting" },
  { value: "partner_meeting", label: "Partner meeting" },
  { value: "tabling", label: "Tabling / Information booth" },
  { value: "follow_up_visit", label: "Follow-up visit" },
];

// Legacy values we still want to render (home_visit, maternal_visit) so old
// records don't display as raw enum strings — but they're no longer offered
// in the picker. Home/maternal visits belong on the Maternal & Family page.
const LEGACY_TYPE_LABELS = {
  home_visit: "Home visit (legacy)",
  maternal_visit: "Maternal visit (legacy)",
};

export default function Outreach() {
  const { can, user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(searchParams.get("new") === "1");
  const [editing, setEditing] = useState(null);
  const [careSeekers, setCareSeekers] = useState([]);
  const [signingId, setSigningId] = useState(null);
  const [participantsFor, setParticipantsFor] = useState(null);

  // Volunteers have their own dedicated portal RSVP flow — keep this page
  // focused on staff (admins, navigators, supervisors, etc.).
  const canSignUp = !!user && user.role !== "volunteer";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? { activity_type: filter } : {};
      const [items, cs] = await Promise.all([
        api.get("/outreach", { params }),
        careSeekers.length === 0 ? api.get("/care-seekers") : Promise.resolve({ data: careSeekers }),
      ]);
      setItems(items.data);
      if (cs.data) setCareSeekers(cs.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [filter, careSeekers]);
  useEffect(() => { load(); }, [load]);

  const deleteEvent = async (event) => {
    const ok = window.confirm(
      `Delete outreach activity "${event.title}"?\n\n` +
      `It will be removed from this list and from any linked Care Seeker timelines. ` +
      `The record stays in the database for recovery — Admin can restore it from Recently Deleted.`
    );
    if (!ok) return;
    try {
      await api.delete(`/outreach/${event.id}`);
      toast.success(`Removed "${event.title}"`);
      setItems((prev) => prev.filter((e) => e.id !== event.id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleSignup = async (event) => {    if (signingId) return;
    const going = event.my_signup_status === "going";
    setSigningId(event.id);
    try {
      if (going) {
        await api.delete(`/outreach/${event.id}/signup`);
        toast.success("Removed yourself from this event");
        setItems((prev) => prev.map((e) => e.id === event.id ? {
          ...e,
          my_signup_status: null,
          staff_signups_count: Math.max(0, (e.staff_signups_count || 1) - 1),
        } : e));
      } else {
        await api.post(`/outreach/${event.id}/signup`, { status: "going" });
        toast.success(`You're signed up for ${event.title}`);
        setItems((prev) => prev.map((e) => e.id === event.id ? {
          ...e,
          my_signup_status: "going",
          staff_signups_count: (e.staff_signups_count || 0) + (event.my_signup_status === "going" ? 0 : 1),
        } : e));
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSigningId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="outreach-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="hw-eyebrow mb-2">In the community</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">Community Outreach</h1>
          <p className="text-[#334155] mt-2 max-w-2xl text-sm">
            Log community events, partner meetings, tabling, and follow-up
            touchpoints. Home visits and maternal visits live on the
            Maternal &amp; Family page.
          </p>
        </div>
        {can("outreach.create") && (
          <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium" data-testid="add-outreach-btn">
            <Plus className="w-4 h-4" /> New activity
          </button>
        )}
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-4">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="w-full sm:w-auto px-3 py-2 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0] text-sm" data-testid="outreach-filter">
          <option value="">All activities</option>
          {ACTIVITY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#1E3A8A]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-12 text-center">
          <HandHeart className="w-10 h-10 text-[#1E3A8A] mx-auto mb-3" strokeWidth={1.5} />
          <h3 className="font-display text-lg font-semibold text-[#0F172A]">No outreach yet</h3>
          <p className="text-sm text-[#1E3A8A]">Capture community events, partner meetings, and outreach activities here.</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="outreach-list">
          {items.map((a) => {
            const going = a.my_signup_status === "going";
            const isPast = a.activity_date && a.activity_date < new Date().toISOString().slice(0, 10);
            return (
            <li key={a.id} className="bg-white border border-[#E5E5E0] rounded-2xl p-5 hw-card-hover" data-testid={`outreach-item-${a.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="font-display font-semibold text-[#0F172A]">{a.title}</h3>
                    <span className="text-[10px] bg-[#E8ECE7] text-[#1E3A8A] px-2 py-0.5 rounded-full capitalize">
                      {LEGACY_TYPE_LABELS[a.activity_type] ||
                        (ACTIVITY_TYPES.find((t) => t.value === a.activity_type)?.label) ||
                        a.activity_type.replace(/_/g, " ")}
                    </span>
                    {going && (
                      <span data-testid={`outreach-going-${a.id}`} className="text-[10px] uppercase tracking-wider bg-[#E8ECE7] text-[#166534] px-2 py-0.5 rounded-full inline-flex items-center gap-1 font-semibold">
                        <Check className="w-3 h-3" /> You&apos;re going
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#1E3A8A]">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(a.activity_date)}</span>
                    {a.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{a.location}</span>}
                    {a.care_seekers_count > 0 && <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{a.care_seekers_count} served</span>}
                    {((a.staff_signups_count || 0) + (a.volunteer_signups_count || 0)) > 0 && (
                      <button
                        type="button"
                        onClick={() => setParticipantsFor(a)}
                        data-testid={`outreach-participants-${a.id}`}
                        className="inline-flex items-center gap-1 underline decoration-dotted hover:text-[#0F172A]"
                        title="See who's going"
                      >
                        <UserPlus className="w-3 h-3" />
                        {(a.staff_signups_count || 0)} staff
                        {(a.volunteer_signups_count || 0) > 0 ? ` · ${a.volunteer_signups_count} volunteer${a.volunteer_signups_count === 1 ? "" : "s"}` : ""}
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {canSignUp && !isPast && (
                    <button
                      onClick={() => toggleSignup(a)}
                      disabled={signingId === a.id}
                      data-testid={`outreach-signup-btn-${a.id}`}
                      className={`text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors disabled:opacity-50 ${
                        going
                          ? "bg-white text-[#9F1239] border border-[#FECACA] hover:bg-[#FEF2F2]"
                          : "bg-[#1E3A8A] text-white hover:bg-[#172554]"
                      }`}
                    >
                      {signingId === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : going ? <UserMinus className="w-3.5 h-3.5" /> : <UserPlus className="w-3.5 h-3.5" />}
                      {going ? "Cancel signup" : "Sign me up"}
                    </button>
                  )}
                  {can("outreach.edit") && (
                    <button
                      onClick={() => setEditing(a)}
                      data-testid={`edit-outreach-${a.id}`}
                      className="text-xs text-[#1E3A8A] inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#F9F8F6] border border-[#E5E5E0] hover:bg-white"
                    >
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                  )}
                  {can("outreach.delete") && (
                    <button
                      onClick={() => deleteEvent(a)}
                      data-testid={`delete-outreach-${a.id}`}
                      title="Delete this outreach activity"
                      className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-red-200 text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  )}
                </div>
              </div>
              {a.notes && <p className="text-sm text-[#334155] mt-3">{a.notes}</p>}
              {a.follow_up_needs && <div className="text-xs mt-2"><strong className="text-[#334155]">Follow-up:</strong> <span className="text-[#1E3A8A]">{a.follow_up_needs}</span></div>}
              <MetadataFooter entity={a} className="mt-3 pt-2 border-t border-[#F0EFEB]" />
            </li>
            );
          })}
        </ul>
      )}

      {showForm && <OutreachForm careSeekers={careSeekers} onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      {editing && (
        <OutreachForm
          careSeekers={careSeekers}
          existing={editing}
          onClose={() => setEditing(null)}
          onCreated={() => { setEditing(null); load(); }}
        />
      )}
      {participantsFor && (
        <ParticipantsDrawer
          event={participantsFor}
          onClose={() => setParticipantsFor(null)}
        />
      )}
    </div>
  );
}

function ParticipantsDrawer({ event, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/outreach/${event.id}/participants`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((e) => { if (!cancelled) setError(formatApiError(e)); });
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { cancelled = true; window.removeEventListener("keydown", onKey); };
  }, [event.id, onClose]);

  const staff = (data?.participants || []).filter((p) => p.kind === "staff");
  const vols = (data?.participants || []).filter((p) => p.kind === "volunteer");

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-[#0F172A]/40" onClick={onClose} data-testid="outreach-participants-drawer">
      <div className="bg-white w-full max-w-md h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-[#E5E5E0] px-5 py-4 flex items-center justify-between">
          <div>
            <div className="hw-eyebrow text-[10px] mb-1">Who&apos;s going</div>
            <h2 className="font-display text-lg font-semibold text-[#0F172A]">{event.title}</h2>
          </div>
          <button onClick={onClose} data-testid="outreach-participants-close" className="p-1.5 rounded-full hover:bg-[#F9F8F6]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          {error ? (
            <div className="text-xs text-[#991B1B]">{error}</div>
          ) : !data ? (
            <div className="text-xs text-[#1E3A8A]">Loading…</div>
          ) : (data.count || 0) === 0 ? (
            <div className="text-xs text-[#7E937D]">No one is signed up yet.</div>
          ) : (
            <>
              <div>
                <div className="hw-eyebrow text-[10px] mb-2">Staff ({staff.length})</div>
                {staff.length === 0 ? (
                  <div className="text-xs text-[#7E937D]">No staff signed up.</div>
                ) : (
                  <ul className="space-y-1.5" data-testid="outreach-participants-staff">
                    {staff.map((p, i) => (
                      <li key={`s-${i}`} className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0]">
                        <div>
                          <div className="text-[13px] font-medium text-[#0F172A]">{p.name || "(unnamed)"}</div>
                          {p.role && <div className="text-[10px] uppercase tracking-wider text-[#1E3A8A]">{p.role}</div>}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <div className="hw-eyebrow text-[10px] mb-2">Volunteers ({vols.length})</div>
                {vols.length === 0 ? (
                  <div className="text-xs text-[#7E937D]">No volunteers RSVPed.</div>
                ) : (
                  <ul className="space-y-1.5" data-testid="outreach-participants-volunteers">
                    {vols.map((p, i) => (
                      <li key={`v-${i}`} className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0]">
                        <div>
                          <div className="text-[13px] font-medium text-[#0F172A]">{p.name || "(unnamed)"}</div>
                          {p.email && <div className="text-[10px] text-[#1E3A8A] break-all">{p.email}</div>}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function OutreachForm({ careSeekers, onClose, onCreated, existing }) {
  const [form, setForm] = useState({
    activity_type: existing?.activity_type || "outreach_event",
    title: existing?.title || "",
    activity_date: existing?.activity_date || new Date().toISOString().slice(0, 10),
    location: existing?.location || "",
    county: existing?.county || "",
    care_seekers_served: existing?.care_seekers_served || [],
    care_seekers_count: existing?.care_seekers_count ?? 0,
    notes: existing?.notes || "",
    follow_up_needs: existing?.follow_up_needs || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!existing;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        care_seekers_count: Number(form.care_seekers_count) || form.care_seekers_served.length,
      };
      if (isEdit) {
        await api.patch(`/outreach/${existing.id}`, payload);
        toast.success("Activity updated");
      } else {
        await api.post("/outreach", payload);
        toast.success("Activity logged");
      }
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F172A]/40">
      <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#E5E5E0] px-6 py-4 flex justify-between">
          <h2 className="font-display text-xl font-semibold">{isEdit ? "Edit" : "New"} Activity</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4" data-testid="outreach-form">
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Type</label>
            <select value={form.activity_type} onChange={(e) => setForm({ ...form, activity_type: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" data-testid="out-type">
              {ACTIVITY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Title</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" data-testid="out-title" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Date</label>
              <input type="date" value={form.activity_date} onChange={(e) => setForm({ ...form, activity_date: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Care Seekers served (count)</label>
              <input type="number" value={form.care_seekers_count} onChange={(e) => setForm({ ...form, care_seekers_count: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" data-testid="out-count" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Location</label>
              <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">County</label>
              <input value={form.county} onChange={(e) => setForm({ ...form, county: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0]" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Follow-up needs</label>
            <textarea value={form.follow_up_needs} onChange={(e) => setForm({ ...form, follow_up_needs: e.target.value })} rows={2} className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0]" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">
              Care Seekers served <span className="text-[#1E3A8A] font-normal">(tap names to link this activity to specific Care Seekers)</span>
            </label>
            {careSeekers.length === 0 ? (
              <div className="text-xs text-[#1E3A8A] italic">No Care Seekers yet. Add one first to link them here.</div>
            ) : (
              <div className="max-h-44 overflow-y-auto border border-[#E5E5E0] rounded-xl p-2 bg-[#F9F8F6] flex flex-wrap gap-1.5" data-testid="out-cs-picker">
                {careSeekers.map((cs) => {
                  const selected = form.care_seekers_served.includes(cs.id);
                  const label = `${cs.preferred_name || cs.first_name} ${cs.last_name}`;
                  return (
                    <button
                      type="button"
                      key={cs.id}
                      onClick={() => {
                        const next = selected
                          ? form.care_seekers_served.filter((id) => id !== cs.id)
                          : [...form.care_seekers_served, cs.id];
                        setForm({ ...form, care_seekers_served: next });
                      }}
                      data-testid={`out-cs-chip-${cs.id}`}
                      className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                        selected
                          ? "bg-[#1E3A8A] text-white"
                          : "bg-white text-[#334155] border border-[#E5E5E0] hover:bg-[#E8ECE7]"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            )}
            {form.care_seekers_served.length > 0 && (
              <div className="text-xs text-[#1E3A8A] mt-1">{form.care_seekers_served.length} linked</div>
            )}
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-3 rounded-full border border-[#E5E5E0] text-[#334155] font-medium">Cancel</button>
            <button type="submit" disabled={submitting} data-testid="out-submit-btn" className="flex-1 py-3 rounded-full bg-[#1E3A8A] text-white font-medium disabled:opacity-60">
              {submitting ? "Saving…" : isEdit ? "Save changes" : "Log activity"}
            </button>
          </div>
        </form>
        {isEdit && (
          <div className="px-6 pb-6 space-y-3">
            <AttachmentsPanel
              parentType="outreach"
              parentId={existing.id}
              mode="gallery"
              title="Photos & documents"
            />
          </div>
        )}
      </div>
    </div>
  );
}
